import mongoose from "mongoose";

import {
  Device,
  Notification,
  NOTIFICATION_PRIORITY,
  NOTIFICATION_TYPE,
  type NotificationPriority,
  type NotificationType,
  School,
  Student,
  Teacher,
  User,
} from "../models";
import { logger } from "../utils/logger";
import { fcmService } from "./fcm-service";

/**
 * Push Notification Service
 * High-level service for sending push notifications to users
 * Handles device management, notification storage, and FCM integration
 */

export interface PushNotificationPayload {
  userId: string | mongoose.Types.ObjectId;
  schoolId?: string | mongoose.Types.ObjectId;
  type: NotificationType;
  title: string;
  message: string;
  priority?: NotificationPriority;
  actionUrl?: string;
  metadata?: Record<string, any>;
  imageUrl?: string;
  sound?: string;
  badge?: number;
}

export interface BatchPushNotificationPayload {
  userIds: (string | mongoose.Types.ObjectId)[];
  schoolId?: string | mongoose.Types.ObjectId;
  type: NotificationType;
  title: string;
  message: string;
  priority?: NotificationPriority;
  actionUrl?: string;
  metadata?: Record<string, any>;
  imageUrl?: string;
  sound?: string;
  badge?: number;
}

class PushNotificationService {
  /**
   * Send push notification to a single user
   */
  async sendToUser(
    payload: PushNotificationPayload,
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const userId =
        typeof payload.userId === "string"
          ? new mongoose.Types.ObjectId(payload.userId)
          : payload.userId;

      // Step 1: Create notification record in database
      let schoolIdValue: mongoose.Types.ObjectId | undefined;
      if (payload.schoolId) {
        schoolIdValue =
          typeof payload.schoolId === "string"
            ? new mongoose.Types.ObjectId(payload.schoolId)
            : payload.schoolId;
      }

      const notification = new Notification({
        userId,
        schoolId: schoolIdValue,
        type: payload.type,
        title: payload.title,
        message: payload.message,
        priority: payload.priority || NOTIFICATION_PRIORITY.MEDIUM,
        actionUrl: payload.actionUrl,
        metadata: payload.metadata || {},
        isRead: false,
      });

      await notification.save();

      // Step 2: Get user's active devices with FCM tokens
      const devices = await Device.find({
        userId,
        isActive: true,
        fcmToken: { $exists: true, $ne: null },
      });

      if (devices.length === 0) {
        logger.info(
          `No active devices with FCM tokens found for user: ${userId.toString()}`,
        );
        return {
          success: true, // Notification saved, but no devices to send to
        };
      }

      // Step 3: Get user preferences
      const user = await User.findById(userId);
      if (!user || !user.isNotificationEnabled) {
        logger.info(
          `Push notifications disabled for user: ${userId.toString()}`,
        );
        return {
          success: true, // Notification saved, but user has disabled push
        };
      }

      // Step 4: Send push notification to all devices
      const fcmTokens = devices
        .map(device => device.fcmToken)
        .filter(
          (token): token is string => token !== null && token !== undefined,
        );

      // Determine sound based on priority
      const sound = payload.sound || this.getSoundForPriority(payload.priority);

      // Send to FCM
      const result = await fcmService.sendToMultipleDevices(
        fcmTokens,
        {
          title: payload.title,
          body: payload.message,
          imageUrl: payload.imageUrl,
        },
        {
          notificationId: notification.id,
          type: payload.type,
          actionUrl: payload.actionUrl || "",
          ...(payload.metadata &&
            Object.keys(payload.metadata).length > 0 && {
              metadata: JSON.stringify(payload.metadata),
            }),
        },
        {
          priority:
            payload.priority === NOTIFICATION_PRIORITY.URGENT
              ? "high"
              : "normal",
          sound,
          badge: payload.badge,
          channelId: this.getChannelIdForType(payload.type),
        },
      );

      // Step 5: Handle failed tokens (deactivate invalid devices)
      if (result.failureCount > 0) {
        await this.handleFailedTokens(result.results);
      }

      logger.info(
        `Push notification sent to user ${userId.toString()}: ${result.successCount} success, ${result.failureCount} failure`,
      );

      return {
        success: result.successCount > 0,
      };
    } catch (error: any) {
      logger.error("Error sending push notification to user:", error);
      return {
        success: false,
        error: error.message || "Failed to send push notification",
      };
    }
  }

  /**
   * Send push notification to multiple users
   */
  async sendToMultipleUsers(payload: BatchPushNotificationPayload): Promise<{
    success: boolean;
    successCount: number;
    failureCount: number;
    errors?: string[];
  }> {
    try {
      const userIds = payload.userIds.map(id =>
        typeof id === "string" ? new mongoose.Types.ObjectId(id) : id,
      );

      const results = await Promise.allSettled(
        userIds.map(userId =>
          this.sendToUser({
            userId,
            schoolId: payload.schoolId,
            type: payload.type,
            title: payload.title,
            message: payload.message,
            priority: payload.priority,
            actionUrl: payload.actionUrl,
            metadata: payload.metadata,
            imageUrl: payload.imageUrl,
            sound: payload.sound,
            badge: payload.badge,
          }),
        ),
      );

      const successCount = results.filter(
        r => r.status === "fulfilled" && r.value.success,
      ).length;
      const failureCount = results.length - successCount;

      const errors = results
        .filter(
          r =>
            r.status === "rejected" ||
            (r.status === "fulfilled" && !r.value.success),
        )
        .map(r =>
          r.status === "rejected"
            ? r.reason?.message
            : (r as PromiseFulfilledResult<any>).value.error,
        )
        .filter(Boolean);

      logger.info(
        `Batch push notification sent: ${successCount} success, ${failureCount} failure`,
      );

      return {
        success: successCount > 0,
        successCount,
        failureCount,
        errors: errors.length > 0 ? errors : undefined,
      };
    } catch (error: any) {
      logger.error("Error sending batch push notifications:", error);
      return {
        success: false,
        successCount: 0,
        failureCount: payload.userIds.length,
        errors: [error.message || "Failed to send batch push notifications"],
      };
    }
  }

  /**
   * Send push notification to all users in a school
   */
  async sendToSchool(
    schoolId: string | mongoose.Types.ObjectId,
    payload: Omit<PushNotificationPayload, "userId" | "schoolId">,
  ): Promise<{
    success: boolean;
    successCount: number;
    failureCount: number;
  }> {
    try {
      const schoolObjectId =
        typeof schoolId === "string"
          ? new mongoose.Types.ObjectId(schoolId)
          : schoolId;

      // Get all users in the school (students and teachers)
      const [students, teachers, school] = await Promise.all([
        Student.find({ schoolId: schoolObjectId, isApproved: true }).select(
          "userId",
        ),
        Teacher.find({ schoolId: schoolObjectId, isApproved: true }).select(
          "userId",
        ),
        School.findById(schoolObjectId).select("userId"),
      ]);

      const userIds: mongoose.Types.ObjectId[] = [
        ...students.map(s => s.userId),
        ...teachers.map(t => t.userId),
      ];

      if (school?.userId) {
        userIds.push(school.userId);
      }

      if (userIds.length === 0) {
        logger.info(`No users found in school: ${schoolObjectId.toString()}`);
        return {
          success: false,
          successCount: 0,
          failureCount: 0,
        };
      }

      const result = await this.sendToMultipleUsers({
        userIds,
        schoolId: schoolObjectId,
        ...payload,
      });

      return result;
    } catch (error: any) {
      logger.error("Error sending push notification to school:", error);
      return {
        success: false,
        successCount: 0,
        failureCount: 0,
      };
    }
  }

  /**
   * Send push notification to all students in a school
   */
  async sendToSchoolStudents(
    schoolId: string | mongoose.Types.ObjectId,
    payload: Omit<PushNotificationPayload, "userId" | "schoolId">,
  ): Promise<{
    success: boolean;
    successCount: number;
    failureCount: number;
  }> {
    try {
      const schoolObjectId =
        typeof schoolId === "string"
          ? new mongoose.Types.ObjectId(schoolId)
          : schoolId;

      const students = await Student.find({
        schoolId: schoolObjectId,
        isApproved: true,
      }).select("userId");

      const userIds = students.map(s => s.userId);

      if (userIds.length === 0) {
        logger.info(
          `No students found in school: ${schoolObjectId.toString()}`,
        );
        return {
          success: false,
          successCount: 0,
          failureCount: 0,
        };
      }

      const result = await this.sendToMultipleUsers({
        userIds,
        schoolId: schoolObjectId,
        ...payload,
      });

      return result;
    } catch (error: any) {
      logger.error(
        "Error sending push notification to school students:",
        error,
      );
      return {
        success: false,
        successCount: 0,
        failureCount: 0,
      };
    }
  }

  /**
   * Send push notification to all teachers in a school
   */
  async sendToSchoolTeachers(
    schoolId: string | mongoose.Types.ObjectId,
    payload: Omit<PushNotificationPayload, "userId" | "schoolId">,
  ): Promise<{
    success: boolean;
    successCount: number;
    failureCount: number;
  }> {
    try {
      const schoolObjectId =
        typeof schoolId === "string"
          ? new mongoose.Types.ObjectId(schoolId)
          : schoolId;

      const teachers = await Teacher.find({
        schoolId: schoolObjectId,
        isApproved: true,
      }).select("userId");

      const userIds = teachers.map(t => t.userId);

      if (userIds.length === 0) {
        logger.info(
          `No teachers found in school: ${schoolObjectId.toString()}`,
        );
        return {
          success: false,
          successCount: 0,
          failureCount: 0,
        };
      }

      const result = await this.sendToMultipleUsers({
        userIds,
        schoolId: schoolObjectId,
        ...payload,
      });

      return result;
    } catch (error: any) {
      logger.error(
        "Error sending push notification to school teachers:",
        error,
      );
      return {
        success: false,
        successCount: 0,
        failureCount: 0,
      };
    }
  }

  /**
   * Send emergency alert notification
   */
  async sendEmergencyAlert(
    userId: string | mongoose.Types.ObjectId,
    schoolId: string | mongoose.Types.ObjectId,
    alertDetails: {
      alertId: string;
      alertType: string;
      location?: string;
      personName: string;
    },
  ): Promise<{ success: boolean; error?: string }> {
    return this.sendToUser({
      userId,
      schoolId,
      type: NOTIFICATION_TYPE.EMERGENCY_ALERT,
      title: "🚨 Emergency Alert",
      message: `${alertDetails.personName} needs urgent assistance${alertDetails.location ? ` at ${alertDetails.location}` : ""}`,
      priority: NOTIFICATION_PRIORITY.URGENT,
      actionUrl: `/alerts/${alertDetails.alertId}`,
      metadata: {
        alertId: alertDetails.alertId,
        alertType: alertDetails.alertType,
        location: alertDetails.location,
      },
      sound: "emergency",
      badge: 1,
    });
  }

  /**
   * Send assignment notification
   */
  async sendAssignmentNotification(
    userId: string | mongoose.Types.ObjectId,
    schoolId: string | mongoose.Types.ObjectId,
    assignmentDetails: {
      assignmentId: string;
      title: string;
      className: string;
      deadline: Date;
    },
  ): Promise<{ success: boolean; error?: string }> {
    return this.sendToUser({
      userId,
      schoolId,
      type: NOTIFICATION_TYPE.ASSIGNMENT_CREATED,
      title: "📝 New Assignment",
      message: `${assignmentDetails.title} in ${assignmentDetails.className}. Due: ${assignmentDetails.deadline.toLocaleDateString()}`,
      priority: NOTIFICATION_PRIORITY.MEDIUM,
      actionUrl: `/assignments/${assignmentDetails.assignmentId}`,
      metadata: {
        assignmentId: assignmentDetails.assignmentId,
        className: assignmentDetails.className,
        deadline: assignmentDetails.deadline.toISOString(),
      },
    });
  }

  /**
   * Send chat message notification
   */
  async sendChatNotification(
    userId: string | mongoose.Types.ObjectId,
    schoolId: string | mongoose.Types.ObjectId,
    chatDetails: {
      chatId: string;
      senderName: string;
      message: string;
    },
  ): Promise<{ success: boolean; error?: string }> {
    return this.sendToUser({
      userId,
      schoolId,
      type: NOTIFICATION_TYPE.CHAT_MESSAGE,
      title: `💬 ${chatDetails.senderName}`,
      message: chatDetails.message,
      priority: NOTIFICATION_PRIORITY.MEDIUM,
      actionUrl: `/chats/${chatDetails.chatId}`,
      metadata: {
        chatId: chatDetails.chatId,
        senderName: chatDetails.senderName,
      },
    });
  }

  /**
   * Handle failed FCM tokens (deactivate invalid devices)
   */
  private async handleFailedTokens(
    results: { token: string; success: boolean; error?: string }[],
  ): Promise<void> {
    try {
      const failedTokens = results
        .filter(r => !r.success && r.error)
        .filter(
          r =>
            r.error?.includes("invalid") ||
            r.error?.includes("not registered") ||
            r.error?.includes("unregistered"),
        )
        .map(r => r.token);

      if (failedTokens.length > 0) {
        await Device.updateMany(
          { fcmToken: { $in: failedTokens } },
          { isActive: false },
        );

        logger.info(`Deactivated ${failedTokens.length} invalid FCM tokens`);
      }
    } catch (error) {
      logger.error("Error handling failed tokens:", error);
    }
  }

  /**
   * Get sound based on notification priority
   */
  private getSoundForPriority(priority?: NotificationPriority): string {
    switch (priority) {
      case NOTIFICATION_PRIORITY.URGENT: {
        return "emergency";
      }
      case NOTIFICATION_PRIORITY.HIGH: {
        return "high_priority";
      }
      default: {
        return "default";
      }
    }
  }

  /**
   * Get Android notification channel ID based on notification type
   */
  private getChannelIdForType(type: NotificationType): string {
    // Map notification types to Android notification channels
    if (
      type === NOTIFICATION_TYPE.EMERGENCY_ALERT ||
      type === NOTIFICATION_TYPE.ESCORT_ALERT
    ) {
      return "emergency_alerts";
    }

    if (
      type === NOTIFICATION_TYPE.ASSIGNMENT_CREATED ||
      type === NOTIFICATION_TYPE.ASSIGNMENT_GRADED ||
      type === NOTIFICATION_TYPE.QUIZ_CREATED
    ) {
      return "academic";
    }

    if (
      type === NOTIFICATION_TYPE.CHAT_MESSAGE ||
      type === NOTIFICATION_TYPE.GROUP_MESSAGE
    ) {
      return "messages";
    }

    return "general";
  }
}

// Export singleton instance
export const pushNotificationService = new PushNotificationService();
